import type { FC } from "react";

import type { UsageEntry } from "../../types/usage";

import { formatTokenCount, formatUsd } from "../../utils/currency";
import { formatDateTime } from "../../utils/time";
import { DataTable } from "../common/DataTable";
import { EntriesTableShell } from "../common/EntriesTableShell";
import { Text } from "../common/Text";
import {
  getMissingPricingInputsTitle,
  getUsagePricingStatusText,
} from "./UsagePricingDetails";

const EMPTY = "\u2014";

const UsageEntryCostCell: FC<{ entry: UsageEntry }> = ({ entry }) => {
  const pricingStatus = getUsagePricingStatusText(entry.costBreakdown);
  const missingTitle = getMissingPricingInputsTitle(
    entry.costBreakdown?.missingPricingInputs,
  );

  return (
    <div className="flex flex-col items-end">
      <span>{formatUsd(entry.costUsd)}</span>
      {pricingStatus ? (
        <span
          className="cursor-default text-[11px] leading-tight text-slate-500 dark:text-slate-400"
          title={missingTitle}
        >
          {pricingStatus}
        </span>
      ) : null}
    </div>
  );
};

const UsageEntryRow: FC<{ entry: UsageEntry }> = ({ entry }) => (
  <DataTable.Row key={entry.entryId}>
    <DataTable.Cell className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
      {formatDateTime(entry.timestampMs)}
    </DataTable.Cell>
    <DataTable.Cell>
      <div className="flex flex-col">
        <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
          {entry.provider}
        </span>
        <Text>{entry.model ?? EMPTY}</Text>
      </div>
    </DataTable.Cell>
    <DataTable.Cell className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
      {entry.stage}
    </DataTable.Cell>
    <DataTable.Cell className="text-right font-mono">
      {formatTokenCount(entry.tokens.totalTokens)}
    </DataTable.Cell>
    <DataTable.Cell className="text-right font-mono">
      <UsageEntryCostCell entry={entry} />
    </DataTable.Cell>
    <DataTable.Cell className="max-w-[220px] truncate font-mono text-xs text-gray-600 dark:text-white/70">
      {entry.threadId ?? EMPTY}
    </DataTable.Cell>
    <DataTable.Cell className="max-w-[220px] truncate font-mono text-xs text-gray-600 dark:text-white/70">
      {entry.taskId}
    </DataTable.Cell>
    <DataTable.Cell className="max-w-[160px] truncate font-mono text-xs text-gray-600 dark:text-white/70">
      {entry.ticketId ?? EMPTY}
    </DataTable.Cell>
  </DataTable.Row>
);

export interface UsageEntriesTableProps {
  entries: UsageEntry[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  errorMessage?: string | null;
  onPageChange: (page: number) => void;
}

export const UsageEntriesTable: FC<UsageEntriesTableProps> = ({
  entries,
  total,
  page,
  pageSize,
  isLoading,
  errorMessage,
  onPageChange,
}) => (
  <EntriesTableShell
    total={total}
    page={page}
    pageSize={pageSize}
    isLoading={isLoading && entries.length === 0}
    errorMessage={errorMessage}
    isEmpty={total === 0 && entries.length === 0 && !isLoading}
    emptyTitle="No usage entries"
    emptyDescription="Try expanding the date range or clearing filters."
    onPageChange={onPageChange}
  >
    <DataTable responsive className="text-xs">
      <DataTable.Header>
        <tr>
          <DataTable.HeaderCell>Timestamp</DataTable.HeaderCell>
          <DataTable.HeaderCell>Provider / Model</DataTable.HeaderCell>
          <DataTable.HeaderCell>Stage</DataTable.HeaderCell>
          <DataTable.HeaderCell className="text-right">
            Tokens
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="text-right">
            Cost
          </DataTable.HeaderCell>
          <DataTable.HeaderCell>Thread</DataTable.HeaderCell>
          <DataTable.HeaderCell>Task</DataTable.HeaderCell>
          <DataTable.HeaderCell>Ticket</DataTable.HeaderCell>
        </tr>
      </DataTable.Header>
      <DataTable.Body>
        {entries.map((entry) => (
          <UsageEntryRow key={entry.entryId} entry={entry} />
        ))}
      </DataTable.Body>
    </DataTable>
  </EntriesTableShell>
);
